import { useEffect, useRef, useState } from 'react';

interface UseInViewOptions extends IntersectionObserverInit {
    freezeOnceVisible?: boolean;
}

export const useInView = <T extends Element>(options: UseInViewOptions = {}) => {
    const { freezeOnceVisible = true, ...observerOptions } = options;
    const targetRef = useRef<T | null>(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const node = targetRef.current;
        if (!node || (freezeOnceVisible && inView)) {
            return;
        }

        if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
            setInView(true);
            return;
        }

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setInView(true);
                    if (freezeOnceVisible) {
                        observer.disconnect();
                    }
                } else if (!freezeOnceVisible) {
                    setInView(false);
                }
            });
        }, observerOptions);

        observer.observe(node);

        return () => {
            observer.disconnect();
        };
    }, [observerOptions, freezeOnceVisible, inView]);

    return { ref: targetRef, inView } as const;
};
